'use client';

import { useEffect, useMemo, useState } from 'react';
import { ProductDetailLevel3Data, ProductVariant } from '../../interfaces';

export interface UseProductDetailTemplateResult {
  selectedVariant: ProductVariant | null;
  selectedAttributes: Record<string, string>;
  carouselKey: number;
  allImages: Array<{
    _key: string;
    alt?: string;
    asset: {
      _ref?: string;
      url: string;
    };
    hotspot?: unknown;
    crop?: unknown;
  }>;
  handleAttributeChange: (attributeId: string, value: string) => void;
  getAvailableValues: (attributeId: string) => string[];
  getAllPossibleValues: (attributeId: string) => string[];
}

export const useProductDetailTemplate = (product: ProductDetailLevel3Data): UseProductDetailTemplateResult => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [carouselKey, setCarouselKey] = useState(0);

  // Reset carousel to first image when variant changes
  useEffect(() => {
    setCarouselKey((prev) => prev + 1);
  }, [selectedVariant]);

  // Get all images for carousel (selected variant images first, then other variant images, then primary + product images)
  const allImages = useMemo(() => {
    const images: UseProductDetailTemplateResult['allImages'] = [];

    const seen = new Set<string>();
    const pushUnique = (img?: UseProductDetailTemplateResult['allImages'][number]) => {
      if (!img) return;
      const id = img.asset._ref || img.asset.url;
      if (!id || seen.has(id)) return;
      seen.add(id);
      images.push(img);
    };

    if (selectedVariant?.variantImages && selectedVariant.variantImages.length > 0) {
      // Variant selected: 1) selected variant images, 2) primary, 3) other variants, 4) product images
      selectedVariant.variantImages.forEach((img, index) => {
        pushUnique({ ...img, _key: img._key || `selected-variant-${index}` });
      });

      if (product.primaryImage) {
        pushUnique({
          _key: 'primary',
          alt: product.primaryImage.alt,
          asset: product.primaryImage.asset,
          hotspot: product.primaryImage.hotspot,
          crop: product.primaryImage.crop,
        });
      }

      if (product.productVariants && product.productVariants.length > 0) {
        product.productVariants.forEach((variant, vIdx) => {
          if (!variant.variantImages || variant.variantImages.length === 0) return;
          variant.variantImages.forEach((img, iIdx) => {
            pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
          });
        });
      }

      if (product.productImages && product.productImages.length > 0) {
        product.productImages.forEach((img, pIdx) => pushUnique({ ...img, _key: img._key || `product-${pIdx}` }));
      }
    } else {
      // No variant selected: 1) primary, 2) all variants, 3) product images
      if (product.primaryImage) {
        pushUnique({
          _key: 'primary',
          alt: product.primaryImage.alt,
          asset: product.primaryImage.asset,
          hotspot: product.primaryImage.hotspot,
          crop: product.primaryImage.crop,
        });
      }

      if (product.productVariants && product.productVariants.length > 0) {
        product.productVariants.forEach((variant, vIdx) => {
          if (!variant.variantImages || variant.variantImages.length === 0) return;
          variant.variantImages.forEach((img, iIdx) => {
            pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
          });
        });
      }

      if (product.productImages && product.productImages.length > 0) {
        product.productImages.forEach((img, pIdx) => pushUnique({ ...img, _key: img._key || `product-${pIdx}` }));
      }
    }

    return images;
  }, [product.primaryImage, product.productImages, product.productVariants, selectedVariant]);

  // Get all possible values for an attribute (regardless of current selections)
  const getAllPossibleValues = (attributeId: string): string[] => {
    const values = new Set<string>();

    // Get all possible values for this attribute from all variants
    product.productVariants.forEach((variant) => {
      const attrValue = variant.attributeValues.find((av) => av.attribute._ref === attributeId);
      if (attrValue) {
        values.add(attrValue.value);
      }
    });

    return Array.from(values);
  };

  // Get available values for an attribute based on current selections
  const getAvailableValues = (attributeId: string): string[] => {
    const values = new Set<string>();

    // For each possible value of this attribute, check if there's at least one compatible variant
    const allPossibleValues = getAllPossibleValues(attributeId);

    allPossibleValues.forEach((value) => {
      // Check if there's at least one variant that:
      // 1. Has this specific value for the current attribute
      // 2. Is compatible with all other selected attributes
      const hasCompatibleVariant = product.productVariants.some((variant) => {
        // First check if this variant has the value we're testing
        const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attributeId);
        if (!variantAttrValue || variantAttrValue.value !== value) {
          return false;
        }

        // Then check if this variant is compatible with all other selected attributes
        return Object.entries(selectedAttributes).every(([selectedAttrId, selectedValue]) => {
          // Skip the current attribute being evaluated
          if (selectedAttrId === attributeId) return true;

          if (!selectedValue) return true; // No selection for this attribute, so it's compatible

          // Find if this variant has this attribute
          const otherVariantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === selectedAttrId);

          // If variant doesn't have this attribute, it's incompatible
          if (!otherVariantAttrValue) return false;

          // If variant has this attribute, it must match the selected value
          return otherVariantAttrValue.value === selectedValue;
        });
      });

      if (hasCompatibleVariant) {
        values.add(value);
      }
    });

    return Array.from(values);
  };

  // Handle attribute selection
  const handleAttributeChange = (attributeId: string, value: string) => {
    const newSelectedAttributes: Record<string, string> = {
      ...selectedAttributes,
      [attributeId]: value,
    };

    // Remove attribute if value is empty
    if (!value) {
      delete newSelectedAttributes[attributeId];
    }

    setSelectedAttributes(newSelectedAttributes);

    // If no attributes selected, clear the selected variant
    if (Object.keys(newSelectedAttributes).length === 0) {
      setSelectedVariant(null);
      return;
    }

    // Determine required attributes from filters if present; otherwise infer from variants
    const requiredAttributeIds: string[] = (() => {
      if (product.attributeFilters && product.attributeFilters.length > 0) {
        return product.attributeFilters.map((f) => f.attribute._ref);
      }
      const ids = new Set<string>();
      product.productVariants.forEach((v) => v.attributeValues.forEach((av) => ids.add(av.attribute._ref)));
      return Array.from(ids);
    })();

    // Prefer exact match when all required are selected, otherwise select only if a unique variant matches current selections
    const allRequiredSelected = requiredAttributeIds.every((id) => Boolean(newSelectedAttributes[id]));

    let matchingVariant: ProductVariant | null = null;
    if (allRequiredSelected) {
      matchingVariant =
        product.productVariants.find((variant) => {
          return requiredAttributeIds.every((attrId) => {
            const selectedValue = newSelectedAttributes[attrId];
            const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
            return variantAttrValue && variantAttrValue.value === selectedValue;
          });
        }) || null;
    } else {
      // Find candidates compatible with all current selections
      const candidates = product.productVariants.filter((variant) => {
        return Object.entries(newSelectedAttributes).every(([attrId, selectedValue]) => {
          if (!selectedValue) return true;
          const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
          return variantAttrValue && variantAttrValue.value === selectedValue;
        });
      });
      matchingVariant = candidates.length === 1 ? candidates[0] : null;
    }

    setSelectedVariant(matchingVariant);
  };

  return {
    selectedVariant,
    selectedAttributes,
    carouselKey,
    allImages,
    handleAttributeChange,
    getAvailableValues,
    getAllPossibleValues,
  };
};
